'use client';

import { useMemo } from 'react';
import useVelocityAccountStore, {
	AccountData,
} from 'src/stores/useVelocityAccountsStore';
import useWalletIsConnected from './useWalletIsConnected';
import useSubAccountKeys from './useSubAccountKeys';

/**
 * This "lazily" fetches sub accounts -> it only returns a new value when the LIST of sub accounts changes (e.g. when a subaccount is created or destroyed)
 * @returns
 */
const useLazySubAccounts = (): AccountData[] => {
	const getAccountState = useVelocityAccountStore((s) => s.get);
	const accountKeys = useSubAccountKeys();

	const isConnected = useWalletIsConnected();

	// Derived synchronously (not via useState + useEffect) so the value is
	// correct on the first render — no empty-array flash on mount. `accountKeys`
	// is memoized on the subaccount key set, so this only recomputes when a
	// subaccount is added/removed or the connection state changes, preserving
	// the "lazy" behaviour.
	return useMemo(() => {
		if (!isConnected) return [];

		return accountKeys
			.map((accountKey) => getAccountState().accounts[accountKey])
			.filter(Boolean) as AccountData[];
	}, [accountKeys, isConnected, getAccountState]);
};

export default useLazySubAccounts;
